
import React from 'react';
import { useLanguage } from '@/context/language-context';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Star } from 'lucide-react';

interface PlanProps {
  title: string;
  price: string;
  description: string;
  features: string[];
  isPopular?: boolean;
  onSelect: () => void;
}

interface PlanFeatureProps {
  feature: string;
}

const PlanFeature: React.FC<PlanFeatureProps> = ({ feature }) => {
  return (
    <div className="flex items-start mb-3">
      <CheckCircle className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
      <span className="text-sm">{feature}</span>
    </div>
  );
};

const SubscriptionPlans: React.FC = () => {
  const { t } = useLanguage();

  const handleSelectPlan = (planId: string) => {
    console.log('Selected plan:', planId);
    // TODO: Implement subscription logic
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold mb-3">{t('subscription.choose_your_plan')}</h2>
        <p className="text-muted-foreground max-w-md mx-auto">
          {t('subscription.choose_plan_description')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
        {/* Basic Plan */}
        <Card className="border">
          <CardHeader className="pb-3">
            <CardTitle className="text-xl">{t('subscription.plans.basic.title')}</CardTitle>
            <div className="mt-1">
              <span className="text-3xl font-bold">{t('subscription.plans.basic.price')}</span>
              <span className="text-muted-foreground text-sm ml-1">/mo</span>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">{t('subscription.plans.basic.description')}</p>
            <div className="space-y-1 mb-6">
              <PlanFeature feature={t('subscription.plans.basic.benefits.messaging')} />
              <PlanFeature feature={t('subscription.plans.basic.benefits.photos')} />
              <PlanFeature feature={t('subscription.plans.basic.benefits.browse')} />
              <PlanFeature feature={t('subscription.plans.basic.benefits.mobile')} />
            </div>
            <Button
              onClick={() => handleSelectPlan('basic')}
              variant="outline"
              className="w-full"
            >
              {t('subscription.select_plan')}
            </Button>
          </CardContent>
        </Card>

        {/* Premium Plan */}
        <Card className="border border-primary shadow-md relative">
          <div className="absolute top-0 inset-x-0 flex justify-center -translate-y-1/2">
            <div className="bg-primary text-primary-foreground text-xs py-1 px-3 rounded-full flex items-center">
              <Star className="h-3 w-3 mr-1" />
              {t('subscription.most_popular')}
            </div>
          </div>
          <CardHeader className="pb-3">
            <CardTitle className="text-xl">{t('subscription.plans.premium.title')}</CardTitle>
            <div className="mt-1">
              <span className="text-3xl font-bold">{t('subscription.plans.premium.price')}</span>
              <span className="text-muted-foreground text-sm ml-1">/mo</span>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">{t('subscription.plans.premium.description')}</p>
            <div className="space-y-1 mb-6">
              <PlanFeature feature={t('subscription.plans.premium.benefits.messaging')} />
              <PlanFeature feature={t('subscription.plans.premium.benefits.photos')} />
              <PlanFeature feature={t('subscription.plans.premium.benefits.browse')} />
              <PlanFeature feature={t('subscription.plans.premium.benefits.mobile')} />
              <PlanFeature feature={t('subscription.plans.premium.benefits.verified')} />
              <PlanFeature feature={t('subscription.plans.premium.benefits.priority')} />
            </div>
            <Button
              onClick={() => handleSelectPlan('premium')}
              className="w-full"
            >
              {t('subscription.select_plan')}
            </Button>
          </CardContent>
        </Card>

        {/* VIP Plan */}
        <Card className="border">
          <CardHeader className="pb-3">
            <CardTitle className="text-xl">{t('subscription.plans.vip.title')}</CardTitle>
            <div className="mt-1">
              <span className="text-3xl font-bold">{t('subscription.plans.vip.price')}</span>
              <span className="text-muted-foreground text-sm ml-1">/mo</span>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">{t('subscription.plans.vip.description')}</p>
            <div className="space-y-1 mb-6">
              <PlanFeature feature={t('subscription.plans.vip.benefits.messaging')} />
              <PlanFeature feature={t('subscription.plans.vip.benefits.photos')} />
              <PlanFeature feature={t('subscription.plans.vip.benefits.browse')} />
              <PlanFeature feature={t('subscription.plans.vip.benefits.mobile')} />
              <PlanFeature feature={t('subscription.plans.vip.benefits.verified')} />
              <PlanFeature feature={t('subscription.plans.vip.benefits.priority')} />
            </div>
            <Button
              onClick={() => handleSelectPlan('vip')}
              variant="outline"
              className="w-full"
            >
              {t('subscription.select_plan')}
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="text-center text-sm text-muted-foreground mt-8">
        <p>{t('subscription.cancel_anytime')}</p>
        <p className="mt-1">{t('subscription.terms_apply')}</p>
      </div>
    </div>
  );
};

export default SubscriptionPlans;
